// src/services/bannerService.js
import apiClient from './api';

export const bannerService = {
  /**
   * Get active banners for display, optionally filtered by location
   * Returns { banners: [], location_settings: {} }
   * @param {string} location - Optional: 'catalog_top', 'right_sidebar', 'left_sidebar'
   */
  getActiveBanners: async (location = null, customerId = null) => {
    try {
      const params = {};
      if (location) {
        params.location = location;
      }
      if (customerId) {
        params.customer_id = customerId;
      }
      const response = await apiClient.get('/products/banners/active/', { params });
      const data = response.data || {};

      // Validate that banners is actually an array (not HTML error page)
      const bannersData = data.banners || data;
      if (!Array.isArray(bannersData)) {
        console.error('[BannerService] Invalid banners response - not an array');
        return {
          success: false,
          banners: [],
          locationSettings: null,
          data: [],
          error: 'Invalid response from server'
        };
      }

      return {
        success: true,
        banners: bannersData,
        locationSettings: data.location_settings || null,
        data: bannersData
      };
    } catch (error) {
      console.error('[BannerService] Error fetching active banners:', error);
      return {
        success: false,
        banners: [],
        locationSettings: null,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch banners'
      };
    }
  },

  /**
   * Get all banners (admin view)
   */
  getAllBanners: async () => {
    try {
      const response = await apiClient.get('/products/banners/');
      return {
        success: true,
        data: response.data.results || response.data || []
      };
    } catch (error) {
      console.error('[BannerService] Error fetching all banners:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch banners'
      };
    }
  },

  /**
   * Get a single banner by ID
   */
  getBanner: async (bannerId) => {
    try {
      const response = await apiClient.get(`/products/banners/${bannerId}/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error fetching banner:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch banner'
      };
    }
  },

  /**
   * Create a new banner
   */
  createBanner: async (bannerData) => {
    try {
      const response = await apiClient.post('/products/banners/', bannerData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error creating banner:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data || 'Failed to create banner'
      };
    }
  },

  /**
   * Update an existing banner
   */
  updateBanner: async (bannerId, bannerData) => {
    try {
      const response = await apiClient.patch(`/products/banners/${bannerId}/`, bannerData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error updating banner:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data || 'Failed to update banner'
      };
    }
  },

  /**
   * Delete a banner
   */
  deleteBanner: async (bannerId) => {
    try {
      await apiClient.delete(`/products/banners/${bannerId}/`);
      return { success: true };
    } catch (error) {
      console.error('[BannerService] Error deleting banner:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete banner'
      };
    }
  },

  /**
   * Copy/duplicate a banner including all settings and images
   * @param {number} bannerId - Banner ID to copy
   */
  copyBanner: async (bannerId) => {
    try {
      const response = await apiClient.post(`/products/banners/${bannerId}/copy/`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error copying banner:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to copy banner'
      };
    }
  },

  /**
   * Upload an image for a banner (appends to existing images)
   * @param {number} bannerId - Banner ID
   * @param {File} file - Image file
   * @param {string} linkUrl - Optional link URL for the image
   * @param {string} linkTarget - Optional link target ('_self' or '_blank')
   */
  uploadBannerImage: async (bannerId, file, linkUrl = null, linkTarget = '_self') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (linkUrl) formData.append('link_url', linkUrl);
      if (linkTarget) formData.append('link_target', linkTarget);

      const response = await apiClient.post(
        `/products/banners/${bannerId}/upload-image/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error uploading banner image:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload image'
      };
    }
  },

  /**
   * Upload an image for a category tile; the tile references it by image_id on save
   * @param {number} bannerId - Banner ID
   * @param {File} file - Image file
   */
  uploadCategoryImage: async (bannerId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post(
        `/products/banners/${bannerId}/upload-category-image/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error uploading category image:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to upload image'
      };
    }
  },

  /**
   * Update an image's metadata (link_url, link_target)
   * @param {number} bannerId - Banner ID
   * @param {number} imageId - MediaAssociation ID
   * @param {object} data - { link_url, link_target }
   */
  updateBannerImage: async (bannerId, imageId, data) => {
    try {
      const response = await apiClient.patch(
        `/products/banners/${bannerId}/update-image/${imageId}/`,
        data
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error updating banner image:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update image'
      };
    }
  },

  /**
   * Delete a banner image by ID
   * @param {number} bannerId - Banner ID
   * @param {number} imageId - MediaAssociation ID
   */
  deleteBannerImage: async (bannerId, imageId) => {
    try {
      await apiClient.delete(`/products/banners/${bannerId}/delete-image/${imageId}/`);
      return { success: true };
    } catch (error) {
      console.error('[BannerService] Error deleting banner image:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete image'
      };
    }
  },

  /**
   * Reorder banner images
   * @param {number} bannerId - Banner ID
   * @param {array} imageIds - Array of MediaAssociation IDs in desired order
   */
  reorderBannerImages: async (bannerId, imageIds) => {
    try {
      const response = await apiClient.post(
        `/products/banners/${bannerId}/reorder-images/`,
        { image_ids: imageIds }
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error reordering banner images:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reorder images'
      };
    }
  },

  /**
   * Get available user groups for targeting
   */
  getUserGroups: async () => {
    try {
      const response = await apiClient.get('/users/groups/');
      return {
        success: true,
        data: response.data.results || response.data || []
      };
    } catch (error) {
      console.error('[BannerService] Error fetching user groups:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch user groups'
      };
    }
  },

  /**
   * Get available banner locations
   */
  getLocations: async () => {
    try {
      const response = await apiClient.get('/products/banners/locations/');
      return {
        success: true,
        data: response.data || []
      };
    } catch (error) {
      console.error('[BannerService] Error fetching locations:', error);
      return {
        success: false,
        data: [],
        error: error.response?.data?.error || 'Failed to fetch locations'
      };
    }
  },

  /**
   * Update location settings (dimensions)
   * @param {number} locationId - Location ID
   * @param {object} data - { height, height_mobile, max_width, aspect_ratio }
   */
  updateLocation: async (locationId, data) => {
    try {
      const response = await apiClient.patch(
        `/products/banners/locations/${locationId}/`,
        data
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error updating location:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update location'
      };
    }
  },

  // ============== TEXT SLIDE METHODS ==============

  /**
   * Add a text slide to a banner
   * @param {number} bannerId - Banner ID
   * @param {object} slideData - { slide_type, background_color, link_url, text_align, vertical_align, text_rows, image_association_id }
   */
  addTextSlide: async (bannerId, slideData) => {
    try {
      const response = await apiClient.post(
        `/products/banners/${bannerId}/add-text-slide/`,
        slideData
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error adding text slide:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add text slide'
      };
    }
  },

  /**
   * Update a text slide
   * @param {number} bannerId - Banner ID
   * @param {number} slideId - Slide ID
   * @param {object} data - Updated slide data
   */
  updateTextSlide: async (bannerId, slideId, data) => {
    try {
      const response = await apiClient.patch(
        `/products/banners/${bannerId}/update-text-slide/${slideId}/`,
        data
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error updating text slide:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update text slide'
      };
    }
  },

  /**
   * Delete a text slide
   * @param {number} bannerId - Banner ID
   * @param {number} slideId - Slide ID
   */
  deleteTextSlide: async (bannerId, slideId) => {
    try {
      await apiClient.delete(`/products/banners/${bannerId}/delete-text-slide/${slideId}/`);
      return { success: true };
    } catch (error) {
      console.error('[BannerService] Error deleting text slide:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete text slide'
      };
    }
  },

  /**
   * Add a text row to a slide
   * @param {number} bannerId - Banner ID
   * @param {number} slideId - Slide ID
   * @param {object} textData - { text, font_size, font_weight, text_color, link_url, margin_bottom }
   */
  addSlideText: async (bannerId, slideId, textData) => {
    try {
      const response = await apiClient.post(
        `/products/banners/${bannerId}/text-slide/${slideId}/add-text/`,
        textData
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error adding slide text:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to add text row'
      };
    }
  },

  /**
   * Update a text row in a slide
   * @param {number} bannerId - Banner ID
   * @param {number} slideId - Slide ID
   * @param {number} textId - Text row ID
   * @param {object} data - Updated text data
   */
  updateSlideText: async (bannerId, slideId, textId, data) => {
    try {
      const response = await apiClient.patch(
        `/products/banners/${bannerId}/text-slide/${slideId}/update-text/${textId}/`,
        data
      );
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('[BannerService] Error updating slide text:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update text row'
      };
    }
  },

  /**
   * Delete a text row from a slide
   * @param {number} bannerId - Banner ID
   * @param {number} slideId - Slide ID
   * @param {number} textId - Text row ID
   */
  deleteSlideText: async (bannerId, slideId, textId) => {
    try {
      await apiClient.delete(`/products/banners/${bannerId}/text-slide/${slideId}/delete-text/${textId}/`);
      return { success: true };
    } catch (error) {
      console.error('[BannerService] Error deleting slide text:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete text row'
      };
    }
  }
};

export default bannerService;
